import React, { useEffect, useState } from 'react';
import { Classes } from '@blueprintjs/core';
import {
  Form,
  FormInstance,
  DateInput,
  TextInput,
  DateSerialized,
  Checkbox,
  FormErrors
} from 'blueprint-form';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';

import { Row, Col } from '../../../../grid';
import Icon from '../../../atoms/icon';
import Button from '../../../molecules/button';
import { DebouncedDialog } from '../../dialog';
import Callout from '../../callout';
import { useTerms } from '../../../../contexts/terms';

const DATE_FORMAT = 'YYYY-MM-DD';

export interface DetailedSearchData {
  id?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  dob?: string;
  mrn?: string;
  includeArchived?: boolean;
}

type DetailedSearchDataRaw = DateSerialized<DetailedSearchData, 'dob'>;

const DEFAULT_DATA: DetailedSearchData = {
  id: '',
  firstName: '',
  lastName: '',
  phone: '',
  dob: null,
  mrn: '',
  includeArchived: false
};

export type DetailedSearchFormInstance = FormInstance<
  DetailedSearchDataRaw,
  DetailedSearchData
>;

interface DetailedSearchFormProps {
  setForm: (form: DetailedSearchFormInstance) => any;
  onSubmit: (data: DetailedSearchData) => any;
}

const REGEX_ID = /^[0-9]+$/;
const REGEX_MRN = /^[0-9a-zA-Z-]+$/;
const REGEX_PHONE = /^\d{10}$/;

const { DeserializeDate, SerializeDate } = FormInstance;

export const DetailedSearchForm: React.FC<DetailedSearchFormProps> = ({
  setForm,
  onSubmit
}) => {
  const { terms } = useTerms();
  const serialize = (data: DetailedSearchDataRaw): DetailedSearchData => {
    return {
      ...data,
      dob: SerializeDate(data.dob, DATE_FORMAT)
    };
  };
  const deserialize = (data: DetailedSearchData): DetailedSearchDataRaw => {
    return {
      ...data,
      dob: DeserializeDate(data.dob, DATE_FORMAT)
    };
  };
  const form = new FormInstance<DetailedSearchDataRaw, DetailedSearchData>(
    deserialize(DEFAULT_DATA),
    {
      serialize,
      deserialize
    }
  );
  const validate = (data: DetailedSearchDataRaw): FormErrors => {
    const errors: FormErrors = {};
    if (data.id && !REGEX_ID.test(data.id.trim())) {
      errors.id = 'The CSID must be digits only!';
    }
    if (data.mrn && !REGEX_MRN.test(data.mrn.trim())) {
      errors.mrn = `The ${terms.mrn.default} must be alphanumeric or hyphen.`;
    }
    if (
      data.phone &&
      !REGEX_PHONE.test(data.phone.trim().replace(/\D+/g, ''))
    ) {
      errors.phone = 'The phone number is not in valid format!';
    }
    if (
      !data.id &&
      !data.mrn &&
      !data.firstName &&
      !data.lastName &&
      !data.phone &&
      !data.dob
    ) {
      errors.id = 'At least 1 field is required!';
    }
    return errors;
  };
  useEffect(() => {
    setForm(form);
  }, []);
  return (
    <Form form={form} validate={validate} onSubmit={onSubmit}>
      <Row>
        <Col sm={12}>
          <TextInput label="CS ID" name="id" fill placeholder="e.g. 123" />
        </Col>
        <Col sm={12}>
          <TextInput
            label={terms.mrn.capitalized}
            name="mrn"
            fill
            placeholder="e.g. E456"
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <TextInput label="First Name" name="firstName" fill />
        </Col>
        <Col sm={12}>
          <TextInput label="Last Name" name="lastName" fill />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <TextInput label="Phone Number" name="phone" fill />
        </Col>
        <Col sm={12}>
          <DateInput
            label="Date of Birth"
            name="dob"
            fill
            format={DATE_FORMAT}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <Checkbox
            label={`Include Archived ${terms.patient.pluralCapitalized}`}
            name="includeArchived"
          />
        </Col>
      </Row>
      <Callout className="mt-4" intent="primary">
        Detailed Search will attempt to match the search fields exactly. If you
        want to do a fuzzy search, use the Keyword Search.
      </Callout>
    </Form>
  );
};

interface SearchPatientDialogProps {
  isOpen: boolean;
  onSearch: (data: DetailedSearchData) => any;
  onClose: () => any;
}

const SearchPatientDialog: React.FC<SearchPatientDialogProps> = ({
  isOpen,
  onSearch,
  onClose
}) => {
  const { terms } = useTerms();
  const [form, setForm] = useState<DetailedSearchFormInstance>(null);
  return (
    <>
      <DebouncedDialog isOpen={isOpen} onClose={onClose} forceScrollY>
        <div
          className={Classes.DIALOG_HEADER}
          style={{
            width: '100%'
          }}
        >
          <h3>Detailed Search</h3>
        </div>
        <div className={Classes.DIALOG_BODY}>
          <DetailedSearchForm setForm={setForm} onSubmit={onSearch} />
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              intent="primary"
              icon={<Icon icon={faSearch} />}
              onClick={() => form?.submit()}
            >
              Search {terms.patient.pluralCapitalized}
            </Button>
          </div>
        </div>
      </DebouncedDialog>
    </>
  );
};

export default SearchPatientDialog;
